import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Play, Pause, Download, RefreshCw, Wifi, FlaskConical, Calendar, Users, ChevronDown, ChevronUp, Settings, Clock, Save, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import CampaignAnalytics from "../components/CampaignAnalytics";

const API = '/api';
const WS_URL = window.location.origin.replace('https://', 'wss://').replace('http://', 'ws://');

export default function CampaignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({});
  const [abStats, setAbStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logFilter, setLogFilter] = useState("");
  const [wsConnected, setWsConnected] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [campContacts, setCampContacts] = useState([]);
  const [campContactsTotal, setCampContactsTotal] = useState(0);
  const [campContactsListName, setCampContactsListName] = useState("");
  const [showContacts, setShowContacts] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [editSettings, setEditSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const wsRef = useRef(null);
  const logEndRef = useRef(null);

  const DAYS = [
    { value: "monday", label: "Lun" },
    { value: "tuesday", label: "Mar" },
    { value: "wednesday", label: "Mie" },
    { value: "thursday", label: "Jue" },
    { value: "friday", label: "Vie" },
    { value: "saturday", label: "Sab" },
    { value: "sunday", label: "Dom" },
  ];

  const fetchData = useCallback(async () => {
    try {
      const [c, l, s] = await Promise.all([
        axios.get(`${API}/campaigns/${id}`),
        axios.get(`${API}/campaigns/${id}/logs?limit=200${logFilter ? `&status=${logFilter}` : ''}`),
        axios.get(`${API}/campaigns/${id}/stats`),
      ]);
      setCampaign(c.data);
      setLogs(l.data.logs);
      setStats(s.data);
      setLastRefresh(new Date());

      if (c.data.is_ab_test) {
        const ab = await axios.get(`${API}/campaigns/${id}/ab-stats`);
        setAbStats(ab.data);
      }

      // Fetch linked contacts
      try {
        const cc = await axios.get(`${API}/campaigns/${id}/contacts?limit=200`);
        setCampContacts(cc.data.contacts);
        setCampContactsTotal(cc.data.total);
        setCampContactsListName(cc.data.list_name);
      } catch (e) {
        // non-critical
      }
    } catch (e) {
      // error silenced
    } finally {
      setLoading(false);
    }
  }, [id, logFilter]);

  // WebSocket connection
  useEffect(() => {
    const connectWs = () => {
      const ws = new WebSocket(`${WS_URL}/api/ws/campaign/${id}`);
      wsRef.current = ws;

      ws.onopen = () => {
        setWsConnected(true);
        // Keep alive
        const ping = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send("ping");
        }, 25000);
        ws._pingInterval = ping;
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === "message_update") {
            // Update stats in real-time
            if (data.stats) setStats(data.stats);
            if (data.ab_stats) {
              // Refresh AB stats
              axios.get(`${API}/campaigns/${id}/ab-stats`).then(r => setAbStats(r.data)).catch(() => {});
            }
            if (data.campaign_status) {
              setCampaign(prev => prev ? { ...prev, status: data.campaign_status } : prev);
            }
            // Add to log feed
            if (data.message) {
              setLogs(prev => {
                const newLog = {
                  id: data.message.id,
                  phone: data.message.phone,
                  contact_name: data.message.contact_name,
                  status: data.message.status,
                  rcs: data.message.rcs,
                  variant: data.message.variant,
                  timestamp: data.message.timestamp,
                  error: data.message.error,
                };
                const filtered = prev.filter(l => l.id !== newLog.id);
                return [newLog, ...filtered].slice(0, 200);
              });
            }
          }
        } catch (e) { /* ignore parse errors */ }
      };

      ws.onclose = () => {
        setWsConnected(false);
        if (ws._pingInterval) clearInterval(ws._pingInterval);
        // Reconnect after 3 seconds
        setTimeout(connectWs, 3000);
      };

      ws.onerror = () => {
        ws.close();
      };
    };

    connectWs();
    return () => {
      if (wsRef.current) {
        if (wsRef.current._pingInterval) clearInterval(wsRef.current._pingInterval);
        wsRef.current.close();
      }
    };
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh polling as fallback (every 8 seconds when campaign is active)
  useEffect(() => {
    if (!campaign || (campaign.status !== 'active' && campaign.status !== 'scheduled')) return;
    const interval = setInterval(() => {
      fetchData();
    }, 8000);
    return () => clearInterval(interval);
  }, [campaign?.status, fetchData]);

  // Auto-scroll log
  useEffect(() => {
    if (logEndRef.current) {
      logEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs]);

  const startCampaign = async () => {
    try {
      await axios.post(`${API}/campaigns/${id}/start`);
      toast.success("Campana iniciada");
      fetchData();
    } catch (e) {
      toast.error("Error al iniciar");
    }
  };

  const pauseCampaign = async () => {
    try {
      await axios.post(`${API}/campaigns/${id}/pause`);
      toast.success("Campana pausada");
      fetchData();
    } catch (e) {
      toast.error("Error al pausar");
    }
  };

  const saveSettingsHandler = async () => {
    if (!editSettings) return;
    setSavingSettings(true);
    try {
      await axios.put(`${API}/campaigns/${id}`, { settings: editSettings });
      toast.success("Configuracion guardada");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally {
      setSavingSettings(false);
    }
  };

  // Init editSettings when campaign loads
  useEffect(() => {
    if (campaign && campaign.settings && !editSettings) {
      setEditSettings({ ...campaign.settings });
    }
  }, [campaign, editSettings]);

  if (loading || !campaign) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const skippedCount = (stats.skipped_no_rcs || 0) + (stats.skipped || 0);
  const errorCount = (stats.error || 0) + (stats.blocked_sms || 0);
  const isCompleted = campaign.status === 'completed';
  const progress = isCompleted ? 100 : (stats.total > 0 ? Math.round(((stats.sent + stats.failed + skippedCount + errorCount) / stats.total) * 100) : 0);
  const VARIANT_COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6"];

  return (
    <div data-testid="campaign-detail-page" className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          data-testid="back-btn"
          onClick={() => navigate("/campaigns")}
          className="p-2 hover:bg-zinc-800 rounded-md transition-colors duration-200 text-zinc-400 hover:text-white"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-3xl font-bold tracking-tight text-white uppercase">
              {campaign.name}
            </h1>
            {campaign.is_ab_test && (
              <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-semibold bg-purple-500/15 text-purple-400 border border-purple-500/20">
                <FlaskConical className="w-3 h-3" /> A/B Test
              </span>
            )}
            {campaign.scheduled_at && campaign.status === 'scheduled' && (
              <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-semibold bg-cyan-500/15 text-cyan-400 border border-cyan-500/20">
                <Calendar className="w-3 h-3" /> Programada
              </span>
            )}
            {campaign.status === 'completed' && (
              <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-semibold bg-emerald-500/15 text-emerald-400 border border-emerald-500/20">
                <CheckCircle className="w-3 h-3" /> Completada
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-0.5">
            <p className="text-sm text-zinc-500 font-mono">ID: {campaign.id.slice(0, 8)}</p>
            <div className="flex items-center gap-1.5">
              <div className={`w-2 h-2 rounded-full ${wsConnected ? 'bg-emerald-500 glow-dot' : 'bg-zinc-600'}`} />
              <span className="text-[10px] font-mono text-zinc-500">
                {wsConnected ? 'WS conectado' : 'WS desconectado'}
              </span>
            </div>
            {lastRefresh && (campaign.status === 'active' || campaign.status === 'scheduled') && (
              <span data-testid="last-refresh-indicator" className="text-[10px] font-mono text-zinc-600">
                Actualizado: {lastRefresh.toLocaleTimeString('es-ES')}
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="refresh-detail-btn"
            onClick={fetchData}
            className="bg-zinc-800 hover:bg-zinc-700 text-zinc-100 border border-zinc-700 rounded-md px-3 py-2 transition-colors duration-200"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          {(campaign.status === 'draft' || campaign.status === 'paused' || campaign.status === 'scheduled') && (
            <button
              data-testid="start-detail-btn"
              onClick={startCampaign}
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-md px-4 py-2 text-sm font-medium flex items-center gap-2 transition-colors duration-200 active:scale-95"
            >
              <Play className="w-4 h-4" /> Iniciar
            </button>
          )}
          {campaign.status === 'active' && (
            <button
              data-testid="pause-detail-btn"
              onClick={pauseCampaign}
              className="bg-yellow-600 hover:bg-yellow-700 text-white rounded-md px-4 py-2 text-sm font-medium flex items-center gap-2 transition-colors duration-200 active:scale-95"
            >
              <Pause className="w-4 h-4" /> Pausar
            </button>
          )}
          {campaign.status === 'completed' && (
            <span
              data-testid="completed-badge"
              className="bg-emerald-900/50 text-emerald-400 border border-emerald-600/30 rounded-md px-4 py-2 text-sm font-medium flex items-center gap-2"
            >
              <CheckCircle className="w-4 h-4" /> Completada
            </span>
          )}
        </div>
      </div>

      {campaign.scheduled_at && campaign.status === 'scheduled' && (
        <div className="bg-cyan-500/5 border border-cyan-500/20 rounded-lg p-4 flex items-center gap-3">
          <Calendar className="w-5 h-5 text-cyan-400" />
          <div>
            <p className="text-sm text-cyan-300 font-medium">Inicio programado</p>
            <p className="text-xs text-zinc-400 font-mono">
              {new Date(campaign.scheduled_at).toLocaleString('es-ES', {
                dateStyle: 'full', timeStyle: 'short'
              })}
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {(() => {
          const sent = stats.sent || 0;
          const sms = (stats.skipped_no_rcs || 0) + (stats.skipped || 0);
          const errors = (stats.failed || 0) + (stats.error || 0) + (stats.blocked_sms || 0);
          const procesados = sent + sms + errors;
          return [
            { label: "Total", value: stats.total || 0, color: "text-white" },
            { label: "Procesados", value: procesados, color: "text-cyan-400" },
            { label: "RCS", value: sent, color: "text-emerald-400" },
            { label: "SMS", value: sms, color: "text-orange-400" },
            { label: "Error", value: errors, color: "text-red-400" },
          ];
        })().map((s) => (
          <div key={s.label} className="bg-zinc-900 border border-white/5 rounded-lg p-4">
            <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{s.label}</p>
            <p className={`text-2xl font-heading font-bold mt-1 ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      <div className="bg-zinc-900 border border-white/5 rounded-lg p-5">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm text-zinc-400">Progreso General</span>
          <span className="text-sm font-mono text-zinc-300">{progress}%</span>
        </div>
        <div className="w-full h-3 bg-zinc-800 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-700 bg-gradient-to-r from-blue-600 to-blue-400"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Analytics & Reports */}
      <CampaignAnalytics
        campaignId={id}
        campaignName={campaign.name}
        stats={stats}
        logs={logs}
      />

      {/* Configuracion de Campana */}
      <div className="bg-zinc-900 border border-white/5 rounded-lg overflow-hidden">
        <button
          data-testid="toggle-settings-btn"
          onClick={() => setShowSettings(!showSettings)}
          className="w-full flex items-center justify-between p-4 hover:bg-white/[0.02] transition-colors duration-200"
        >
          <div className="flex items-center gap-2">
            <Settings className="w-4 h-4 text-blue-400" />
            <h3 className="font-heading text-sm font-semibold uppercase tracking-wider text-zinc-400">
              Configuracion de Campana
            </h3>
          </div>
          {showSettings ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
        </button>
        {showSettings && editSettings && (
          <div className="border-t border-white/5 p-5 space-y-5">
            {/* Active Days */}
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 block mb-2">
                <Calendar className="w-3 h-3 inline mr-1" />Dias Activos
              </label>
              <div className="flex gap-2">
                {DAYS.map(d => (
                  <button
                    key={d.value}
                    data-testid={`camp-day-${d.value}`}
                    onClick={() => {
                      const days = editSettings.active_days || [];
                      setEditSettings({
                        ...editSettings,
                        active_days: days.includes(d.value) ? days.filter(x => x !== d.value) : [...days, d.value]
                      });
                    }}
                    className={`w-10 h-10 rounded-lg text-xs font-bold transition-colors duration-200 ${
                      (editSettings.active_days || []).includes(d.value)
                        ? 'bg-blue-600 text-white'
                        : 'bg-zinc-800 text-zinc-500 hover:bg-zinc-700'
                    }`}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Active Hours */}
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 block mb-2">
                <Clock className="w-3 h-3 inline mr-1" />Horarios Activos
              </label>
              <div className="space-y-2">
                {(editSettings.active_hours || []).map((slot, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      type="time"
                      value={slot.start}
                      onChange={e => {
                        const hours = [...(editSettings.active_hours || [])];
                        hours[i] = { ...hours[i], start: e.target.value };
                        setEditSettings({ ...editSettings, active_hours: hours });
                      }}
                      className="bg-zinc-950/50 border border-zinc-800 rounded-md px-2 py-1.5 text-sm text-white font-mono [color-scheme:dark]"
                    />
                    <span className="text-zinc-500 text-xs">a</span>
                    <input
                      type="time"
                      value={slot.end}
                      onChange={e => {
                        const hours = [...(editSettings.active_hours || [])];
                        hours[i] = { ...hours[i], end: e.target.value };
                        setEditSettings({ ...editSettings, active_hours: hours });
                      }}
                      className="bg-zinc-950/50 border border-zinc-800 rounded-md px-2 py-1.5 text-sm text-white font-mono [color-scheme:dark]"
                    />
                    {(editSettings.active_hours || []).length > 1 && (
                      <button
                        onClick={() => {
                          const hours = (editSettings.active_hours || []).filter((_, j) => j !== i);
                          setEditSettings({ ...editSettings, active_hours: hours });
                        }}
                        className="text-red-500 hover:text-red-400 text-xs font-bold px-2"
                      >X</button>
                    )}
                  </div>
                ))}
                <button
                  onClick={() => {
                    const hours = [...(editSettings.active_hours || []), { start: "08:00", end: "20:00" }];
                    setEditSettings({ ...editSettings, active_hours: hours });
                  }}
                  className="text-blue-400 text-xs hover:text-blue-300"
                >+ Agregar franja horaria</button>
              </div>
            </div>

            {/* RCS Mode + Timing in one row */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 block mb-2">Modo</label>
                <select
                  data-testid="camp-rcs-mode"
                  value={editSettings.rcs_only ? 'rcs' : editSettings.prefer_rcs !== false ? 'both' : 'sms'}
                  onChange={e => {
                    const v = e.target.value;
                    setEditSettings({
                      ...editSettings,
                      rcs_only: v === 'rcs',
                      prefer_rcs: v !== 'sms'
                    });
                  }}
                  className="w-full bg-zinc-950/50 border border-zinc-800 rounded-md px-2 py-1.5 text-sm text-white [color-scheme:dark]"
                >
                  <option value="both">RCS + SMS</option>
                  <option value="rcs">Solo RCS</option>
                  <option value="sms">Solo SMS</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 block mb-2">Delay Min (s)</label>
                <input
                  type="number"
                  value={editSettings.delay_min || 60}
                  onChange={e => setEditSettings({ ...editSettings, delay_min: parseInt(e.target.value) || 60 })}
                  className="w-full bg-zinc-950/50 border border-zinc-800 rounded-md px-2 py-1.5 text-sm text-white font-mono [color-scheme:dark]"
                />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 block mb-2">Delay Max (s)</label>
                <input
                  type="number"
                  value={editSettings.delay_max || 180}
                  onChange={e => setEditSettings({ ...editSettings, delay_max: parseInt(e.target.value) || 180 })}
                  className="w-full bg-zinc-950/50 border border-zinc-800 rounded-md px-2 py-1.5 text-sm text-white font-mono [color-scheme:dark]"
                />
              </div>
            </div>

            {/* Save Button */}
            <button
              data-testid="save-campaign-settings-btn"
              onClick={saveSettingsHandler}
              disabled={savingSettings}
              className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-md px-4 py-2 text-sm font-medium flex items-center gap-2 transition-colors duration-200"
            >
              <Save className="w-4 h-4" />
              {savingSettings ? 'Guardando...' : 'Guardar Configuracion'}
            </button>
          </div>
        )}
      </div>

      {/* Contactos Vinculados */}
      <div className="bg-zinc-900 border border-white/5 rounded-lg overflow-hidden">
        <button
          data-testid="toggle-contacts-btn"
          onClick={() => setShowContacts(!showContacts)}
          className="w-full flex items-center justify-between p-4 hover:bg-white/[0.02] transition-colors duration-200"
        >
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-blue-400" />
            <h3 className="font-heading text-sm font-semibold uppercase tracking-wider text-zinc-400">
              Contactos Vinculados
            </h3>
            <span className="text-xs font-mono text-zinc-500">
              ({campContactsTotal}) {campContactsListName && `· ${campContactsListName}`}
            </span>
          </div>
          {showContacts ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
        </button>
        {showContacts && (
          <div className="border-t border-white/5 max-h-72 overflow-y-auto">
            {campContacts.length > 0 ? (
              <table className="w-full">
                <thead className="sticky top-0 bg-zinc-900/95 backdrop-blur">
                  <tr className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold border-b border-white/5">
                    <th className="py-2 px-4 text-left w-10">#</th>
                    <th className="py-2 px-4 text-left">Teléfono</th>
                    <th className="py-2 px-4 text-left">Nombre</th>
                    {campContacts[0]?.custom_vars && Object.keys(campContacts[0].custom_vars).length > 0 &&
                      Object.keys(campContacts[0].custom_vars).map(k => (
                        <th key={k} className="py-2 px-4 text-left">{k}</th>
                      ))
                    }
                  </tr>
                </thead>
                <tbody>
                  {campContacts.map((c, i) => (
                    <tr key={c.id || i} className="border-t border-white/5 hover:bg-white/[0.02]">
                      <td className="py-1.5 px-4 text-xs font-mono text-zinc-600">{i + 1}</td>
                      <td className="py-1.5 px-4 text-xs font-mono text-blue-300">{c.phone}</td>
                      <td className="py-1.5 px-4 text-xs text-zinc-300">{c.name || '-'}</td>
                      {c.custom_vars && Object.keys(c.custom_vars).length > 0 &&
                        Object.keys(campContacts[0]?.custom_vars || {}).map(k => (
                          <td key={k} className="py-1.5 px-4 text-xs text-zinc-500 max-w-[150px] truncate">{c.custom_vars?.[k] || '-'}</td>
                        ))
                      }
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-6">
                <p className="text-zinc-500 text-sm">No se encontraron contactos</p>
                <p className="text-zinc-600 text-xs mt-1">Verifica que la lista de contactos tenga datos importados</p>
              </div>
            )}
            {campContactsTotal > campContacts.length && (
              <p className="text-center text-xs text-zinc-600 py-2 border-t border-white/5">
                Mostrando {campContacts.length} de {campContactsTotal} contactos
              </p>
            )}
          </div>
        )}
      </div>

      {/* A/B Testing Results */}
      {abStats && abStats.is_ab_test && abStats.variants && (
        <div data-testid="ab-test-results" className="bg-zinc-900 border border-purple-500/20 rounded-lg p-5">
          <div className="flex items-center gap-2 mb-4">
            <FlaskConical className="w-4 h-4 text-purple-400" />
            <h3 className="font-heading text-sm font-semibold uppercase tracking-wider text-purple-300">
              Resultados A/B Test
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {abStats.variants.map((v, idx) => (
              <div key={v.variant} className="bg-zinc-950/50 rounded-lg p-4 border border-white/5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold text-white"
                      style={{ backgroundColor: VARIANT_COLORS[idx % VARIANT_COLORS.length] }}
                    >
                      {v.variant}
                    </div>
                    <span className="text-sm text-zinc-300 font-medium">{v.template_name}</span>
                  </div>
                  <span className={`text-lg font-heading font-bold ${
                    v.success_rate >= 90 ? 'text-emerald-400' :
                    v.success_rate >= 50 ? 'text-amber-400' : 'text-red-400'
                  }`}>
                    {v.success_rate}%
                  </span>
                </div>
                <div className="flex gap-4 text-xs">
                  <span className="text-zinc-500">Asignados: <span className="text-zinc-300 font-mono">{v.total_assigned}</span></span>
                  <span className="text-zinc-500">Enviados: <span className="text-emerald-400 font-mono">{v.sent}</span></span>
                  <span className="text-zinc-500">Fallidos: <span className="text-red-400 font-mono">{v.failed}</span></span>
                </div>
              </div>
            ))}
          </div>
          <div className="h-40">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={abStats.variants}>
                <XAxis dataKey="variant" axisLine={false} tickLine={false} tick={{ fill: '#94A3B8', fontSize: 12, fontWeight: 'bold' }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748B', fontSize: 10 }} />
                <Tooltip
                  contentStyle={{ background: '#18181B', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', fontSize: '12px' }}
                />
                <Bar dataKey="success_rate" name="Tasa de Exito (%)" radius={[4, 4, 0, 0]}>
                  {abStats.variants.map((_, idx) => (
                    <Cell key={idx} fill={VARIANT_COLORS[idx % VARIANT_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="bg-zinc-900 border border-white/5 rounded-lg p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <h3 className="font-heading text-sm font-semibold uppercase tracking-wider text-zinc-400">
              Log de Mensajes
            </h3>
            {wsConnected && (
              <span className="inline-flex items-center gap-1 text-[9px] font-mono text-emerald-500">
                <Wifi className="w-3 h-3" /> EN VIVO
              </span>
            )}
          </div>
          <div className="flex gap-1">
            {["", "sent", "failed", "skipped_no_rcs", "pending", "queued"].map((f) => (
              <button
                key={f}
                data-testid={`filter-${f || 'all'}`}
                onClick={() => setLogFilter(f)}
                className={`px-2.5 py-1 rounded text-[10px] font-mono uppercase transition-colors duration-200 ${
                  logFilter === f
                    ? 'bg-blue-600/20 text-blue-400'
                    : 'text-zinc-500 hover:text-zinc-300'
                }`}
              >
                {f === "" ? "Todos" : f === "sent" ? "Enviados" : f === "failed" ? "Fallidos" : f === "skipped_no_rcs" ? "Sin RCS" : f === "pending" ? "Pendientes" : "Cola"}
              </button>
            ))}
          </div>
        </div>
        <div className="terminal-log max-h-80 overflow-y-auto">
          {logs.length > 0 ? logs.map((log) => (
            <div key={log.id} className="flex gap-3 py-1 items-baseline">
              <span className="log-timestamp whitespace-nowrap text-[11px]">
                [{log.timestamp ? new Date(log.timestamp).toLocaleTimeString('es-ES') : '--:--'}]
              </span>
              <span className={`text-[11px] font-bold min-w-[60px] ${
                log.status === 'sent' ? 'log-sent' :
                log.status === 'failed' ? 'log-failed' :
                log.status === 'skipped_no_rcs' || log.status === 'skipped' ? 'text-orange-400' :
                log.status === 'queued' ? 'log-info' : 'log-pending'
              }`}>
                {log.status === 'sent' ? 'ENVIADO' : log.status === 'failed' ? 'FALLIDO' :
                 log.status === 'skipped_no_rcs' ? 'SIN RCS' : log.status === 'skipped' ? 'SALTADO' :
                 log.status === 'queued' ? 'EN COLA' : 'PENDIENTE'}
              </span>
              {log.variant && (
                <span className="text-[10px] font-mono text-purple-400 bg-purple-500/10 px-1 rounded">
                  {log.variant}
                </span>
              )}
              <span className="text-zinc-400 text-[11px] font-mono">{log.phone}</span>
              <span className="text-zinc-600 text-[11px] truncate">{log.contact_name}</span>
              {log.error && <span className="text-red-500 text-[10px] truncate">{log.error}</span>}
            </div>
          )) : (
            <p className="text-zinc-600 text-center py-4">Sin mensajes en el log</p>
          )}
          <div ref={logEndRef} />
        </div>
      </div>
    </div>
  );
}
